import type {
    AccessTokenCallback,
    ExtraSigninRequestArgs,
    SignoutRedirectArgs,
    SignoutResponse,
    User,
    UserManagerSettings,
} from 'oidc-client-ts';
import {
    UserManager,
    WebStorageStateStore,
} from 'oidc-client-ts';

import { getIdentityAuthority } from './authority';
import {
    removeIdentityCookie,
    setIdentityCookie,
} from './cookies';
import {
    isDebug,
    setDebuggingLogger,
} from './debug';
import type { ErrorFn } from './error';
import {
    defaultErrorHandler,
    WellKnownAuthErrors,
} from './error';
import { InMemoryUser } from './memoryUser';
import { SignoutState } from './signout';
import { hasValidSession } from './token';

type UserManagerSettingsOverrides = Pick<UserManagerSettings, 'client_id' | 'scope'>;

interface AdditionalIdentityUserManagerSettings {
    authority?: string;
    isMsi?: boolean;
    storagePrefix?: string;
    overrides?: UserManagerSettingsOverrides;
    /**
     * this property hardcodes the silent redirect uri to `/portal_/api/tokenFactory/callback`
     * which is a small optimization to load a small snippet of javascript in the iframe during callback
     * instead of loading the entire application in the callback
     */
    usePortalSilentRedirectUri?: boolean;
    onSigninError?: ErrorFn;
}

interface StateArgs {
    state: State;
}

interface OrganizationArgs {
    organizationName?: string;
}

/**
 * Interface for any state that should be persistent through a sign in
 * redirect.
 *
 * @requires `returnTo` - The path to navigate to after callback.
 */
export interface State {
    /**
     * The path to navigate to after callback.
     *
     * This is different from `redirect_uri` which is configured on the module/provider as this
     * value is the path where the user first initiated the sign in request.
     *
     * Note: this is required for deep links to work with authentication.
     */
    returnTo: string;
    [key: string]: any;
}

export type SigninArgsWithState = Omit<ExtraSigninRequestArgs, 'state'> & StateArgs & OrganizationArgs;

export type IdentityUserManagerSettings = AdditionalIdentityUserManagerSettings & Pick<UserManagerSettings,
| 'redirect_uri'
| 'silent_redirect_uri'
| 'post_logout_redirect_uri'
| 'metadata'
| 'metadataUrl'
| 'monitorSession'
| 'iframeScriptOrigin'
| 'signingKeys'
| 'acr_values'>;

export const UberClientId = '1119a927-10ab-4543-bd1a-ad6bfbbc27f4';

export const portalSilentRedirectUri = '/portal_/api/tokenFactory/callback';

// this is a special case that returns all registered scopes for the client
const defaultScopes = [
    'openid',
    'profile',
    'offline_access',
];

export class IdentityUserManager extends UserManager {
    private _user = new InMemoryUser();
    private _failedToFetchCount = 0;
    private _refreshPromise: Promise<User> | null = null;
    private _signoutState = new SignoutState();
    private _onSigninError: ErrorFn;

    constructor(settings: IdentityUserManagerSettings) {
        const authority = settings.authority ?? getIdentityAuthority(settings.isMsi); // TODO: remove ability to customize authority
        const origin = new URL(authority).origin;

        // opt-in optimization to allow loading javascript snippet from portal backend
        // instead of loading entire application in an iframe
        const silent_redirect_uri = settings.usePortalSilentRedirectUri ? `${origin}${portalSilentRedirectUri}` : settings.redirect_uri;
        const post_logout_redirect_uri = settings.post_logout_redirect_uri ?? `${origin}/portal_/cloudrpa`;

        const client_id = (settings.overrides?.client_id ?? UberClientId).toLowerCase();
        const scope = settings.overrides?.scope ?? defaultScopes.join(' ');

        super({
            ...settings,
            client_id,
            scope,
            authority,
            monitorSession: settings.monitorSession ?? true,
            stopCheckSessionOnError: true,
            response_type: 'code', // force pkce,
            userStore: new WebStorageStateStore({
                store: window.sessionStorage, // force session storage
                prefix: settings.storagePrefix,
            }),
            silent_redirect_uri,
            post_logout_redirect_uri,
        });

        this._onSigninError = settings.onSigninError ?? defaultErrorHandler;

        if (isDebug()) {
            setDebuggingLogger();
        }

        // IMPORTANT: bind this to custom async functions to maintain `this` context
        this.signinComplete = this.signinComplete.bind(this);
        this._handleSigninError = this._handleSigninError.bind(this);
        this._expireSession = this._expireSession.bind(this);
        this._onFailedToFetch = this._onFailedToFetch.bind(this);
        this._onNetworkChange = this._onNetworkChange.bind(this);
        this._onSilentRenewError = this._onSilentRenewError.bind(this);

        this.registerCustomEvents();
    }

    registerCustomEvents() {
        window.addEventListener('online', this._onNetworkChange);

        this.events.addUserSignedOut(this._expireSession);
        this.events.addUserLoaded(setIdentityCookie);
        this.events.addAccessTokenExpired(this.signinComplete as AccessTokenCallback);
        this.events.addSilentRenewError(this._onSilentRenewError);
    }

    unregisterCustomEvents() {
        window.removeEventListener('online', this._onNetworkChange);

        this.events.removeUserSignedOut(this._expireSession);
        this.events.removeUserLoaded(setIdentityCookie);
        this.events.removeAccessTokenExpired(this.signinComplete as AccessTokenCallback);
        this.events.removeSilentRenewError(this._onSilentRenewError);
    }

    async getUser(): Promise<User | null> {
        const user = await super.getUser();

        // set window.User if not present
        if (user && !this._user.get()) {
            this._user.set(user);
        }

        return user;
    }

    async storeUser(user: User | null): Promise<void> {
        await super.storeUser(user);
        this._user.set(user);

        if (user) {
            // we have successfully completed a callback, reset the counter
            this._failedToFetchCount = 0;

            setIdentityCookie();
        }
    }

    async removeUser(): Promise<void> {
        removeIdentityCookie();
        this._user.remove();
        return super.removeUser();
    }

    async signinRedirect(args?: SigninArgsWithState | undefined): Promise<void> {
        let state = args?.state;

        if (!state?.returnTo) {
            this._logger.warn('no returnTo provided, using default');
            state = { returnTo: location.pathname + location.search };
        }

        if (args?.organizationName) {
            args.acr_values = `tenantName:${args.organizationName}`;
            delete args.organizationName;
        }

        return super.signinRedirect({
            ...args,
            state,
        });
    }

    async signinCallback(url?: string | undefined): Promise<void | User> {
        try {
            return super.signinCallback(url);
        } catch (error) {
            await this._handleSigninError(error, { state: { returnTo: '/' } });
        }
    }

    async signoutRedirect(args?: SignoutRedirectArgs | undefined): Promise<void> {
        this._signoutState.isSigningOut = true;
        return super.signoutRedirect(args);
    }

    async signoutRedirectCallback(url?: string | undefined): Promise<SignoutResponse> {
        this._signoutState.isSigningOut = false;
        return super.signoutRedirectCallback(url);
    }

    async signinComplete(args?: SigninArgsWithState): Promise<void> {
        try {
            await this.clearStaleState();

            if (this._signoutState.isSigningOut) {
                return;
            }

            if (hasValidSession()) {
                await this.signinSilent(args);
            } else {
                await this.signinRedirect(args);
            }
        } catch (error) {
            await this._handleSigninError(error, args);
        }
    }

    protected async _useRefreshToken(state: any) {
        if (this._refreshPromise) {
            return this._refreshPromise;
        }

        this._refreshPromise = super._useRefreshToken(state);

        try {
            const user = await this._refreshPromise;
            return user;
        } finally {
            // use finally here in case the promise is rejected
            this._refreshPromise = null;
        }
    }

    private async _handleSigninError(error: unknown, args?: SigninArgsWithState): Promise<void> {
        let message = 'unknown';

        if (error instanceof Error) {
            message = error.message;
        }

        switch (message) {
            case WellKnownAuthErrors.NoMatchingState:
            case WellKnownAuthErrors.IFrameTimeout:
            case WellKnownAuthErrors.LoginRequired:
                this._logger.warn(`Well known error occurred: ${message} - retrying...`);
                await this.signinRedirect(args);
                break;
            case WellKnownAuthErrors.InvalidGrant:
                await this.removeUser();
                await this.signinSilent();
                break;
            case WellKnownAuthErrors.FailedToFetch:
                // do nothing, service will retry
                break;
            default: {
                this._logger.error(`Unknown error occurred: ${error}`);
                this._onSigninError(error);
            }
        }
    }

    private async _expireSession() {
        // first, get user from storage to get id_token
        const user = await this.getUser();

        // then clear out storage and timers
        sessionStorage.clear();
        this.stopSilentRenew();

        // finally, sign the user out
        await this.signoutRedirect({ id_token_hint: user?.id_token });
    }

    private async _onNetworkChange() {
        this._logger.debug('Network change detected, validating user object');

        const user = await this.getUser();

        if (!user || user.expired) {
            this._logger.debug('Stale or missing user object detected, signing in silently...');
            await this.signinSilent();
        }
    }

    private _onFailedToFetch(error: unknown) {
        this._failedToFetchCount++;

        this._logger.warn(`Failed to fetch count: ${this._failedToFetchCount}`);

        // The error handler above does nothing when failed to fetch is thrown.
        // To prevent an unrecoverable condition or infinite loop, the failed to
        // fetch counter ensures that if there are 3 or more failures to fetch
        // we navigate to an error page.
        if (this._failedToFetchCount >= 3) {
            this._onSigninError(error);
            return;
        }

        // trigger the online event and attempt to sign in again
        setTimeout(() => {
            window.dispatchEvent(new Event('online'));
        }, 5000 * this._failedToFetchCount);
    }

    private _onSilentRenewError(error: Error) {
        if (error.message === WellKnownAuthErrors.FailedToFetch) {
            this._onFailedToFetch(error);
        }
    }
}
